import axios from "axios";
import api from "services/api";
import type {
  AtualizarContaPagarPayload,
  ContasPagarApiResponse,
  ContasPagarFiltros,
  CriarContaPagarPayload,
  DespesaRecorrenteApi,
  SugerirImpostoResponse,
} from "../types/contas-pagar.types";

export function montarParamsContasPagar(
  filtros: ContasPagarFiltros,
): Record<string, string | number | boolean> {
  const p: Record<string, string | number | boolean> = {};
  p.page = filtros.page ?? 1;
  p.limit = filtros.limit ?? 20;

  if (filtros.status?.trim()) p.status = filtros.status.trim();
  if (filtros.vencimentoInicio?.trim())
    p.vencimentoInicio = filtros.vencimentoInicio.trim();
  if (filtros.vencimentoFim?.trim()) p.vencimentoFim = filtros.vencimentoFim.trim();
  if (filtros.categoria?.trim()) p.categoria = filtros.categoria.trim();
  if (filtros.contexto?.trim()) p.contexto = filtros.contexto.trim();
  if (filtros.naturezaDespesa?.trim())
    p.naturezaDespesa = filtros.naturezaDespesa.trim();
  if (filtros.centroCustoId?.trim())
    p.centroCustoId = filtros.centroCustoId.trim();
  if (filtros.fornecedorId?.trim())
    p.fornecedorId = filtros.fornecedorId.trim();
  if (filtros.busca?.trim()) p.busca = filtros.busca.trim();
  if (filtros.apenasVencidos === true) p.apenasVencidos = true;
  if (filtros.eventoId != null && filtros.eventoId >= 1)
    p.eventoId = filtros.eventoId;

  return p;
}

export async function listarContasPagar(filtros: ContasPagarFiltros): Promise<{
  data: ContasPagarApiResponse | null;
  status: number;
}> {
  const res = await api.get<ContasPagarApiResponse>(
    "/loja/financeiro/titulos-pagar",
    { params: montarParamsContasPagar(filtros) },
  );
  return { data: res.data ?? null, status: res.status };
}

export async function criarContaPagar(
  payload: CriarContaPagarPayload,
): Promise<{ data: { id: string } | null; status: number }> {
  const res = await api.post<{ id: string }>(
    "/loja/financeiro/titulos-pagar",
    payload,
  );
  return { data: res.data ?? null, status: res.status };
}

export async function atualizarContaPagar(
  id: string,
  payload: AtualizarContaPagarPayload,
): Promise<{ data: { id: string } | null; status: number; error?: string }> {
  try {
    const res = await api.patch<{ id: string }>(
      `/loja/financeiro/titulos-pagar/${encodeURIComponent(id)}`,
      payload,
    );
    return { data: res.data ?? null, status: res.status };
  } catch (e: unknown) {
    const msg = axios.isAxiosError(e)
      ? (e.response?.data as { error?: string } | undefined)?.error ??
        e.message
      : e instanceof Error
        ? e.message
        : "Falha ao atualizar título.";
    return {
      data: null,
      status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0,
      error: msg,
    };
  }
}

export async function sugerirImpostoContaPagar(params: {
  eventoId?: number;
  competenciaInicio?: string;
  competenciaFim?: string;
  aliquota: number;
}): Promise<{ data: SugerirImpostoResponse | null; status: number }> {
  const res = await api.get<SugerirImpostoResponse>(
    "/loja/financeiro/titulos-pagar/sugerir-imposto",
    { params },
  );
  return { data: res.data ?? null, status: res.status };
}

export async function listarDespesasRecorrentes(
  opts?: { ativo?: boolean; eventoId?: number },
): Promise<{
  data: DespesaRecorrenteApi[] | null;
  status: number;
}> {
  const params: Record<string, string | number> = {};
  if (opts?.ativo === true) params.ativo = "true";
  if (opts?.ativo === false) params.ativo = "false";
  if (opts?.eventoId != null && opts.eventoId >= 1) {
    params.eventoId = opts.eventoId;
  }
  const res = await api.get<DespesaRecorrenteApi[]>(
    "/loja/financeiro/despesas-recorrentes",
    { params },
  );
  return { data: res.data ?? null, status: res.status };
}

export async function criarDespesaRecorrente(
  payload: Record<string, unknown>,
): Promise<{ data: DespesaRecorrenteApi | null; status: number }> {
  const res = await api.post<DespesaRecorrenteApi>(
    "/loja/financeiro/despesas-recorrentes",
    payload,
  );
  return { data: res.data ?? null, status: res.status };
}

export async function atualizarDespesaRecorrente(
  id: string,
  payload: Record<string, unknown>,
): Promise<{ data: DespesaRecorrenteApi | null; status: number }> {
  const res = await api.patch<DespesaRecorrenteApi>(
    `/loja/financeiro/despesas-recorrentes/${id}`,
    payload,
  );
  return { data: res.data ?? null, status: res.status };
}

export async function gerarDespesasFixasMes(
  mes: number,
  ano: number,
): Promise<{
  data: { gerados: number; ignorados: number; ids: string[] } | null;
  status: number;
}> {
  const res = await api.post<{ gerados: number; ignorados: number; ids: string[] }>(
    "/loja/financeiro/despesas-recorrentes/gerar",
    { mes, ano },
  );
  return { data: res.data ?? null, status: res.status };
}
